import React, { memo, useMemo } from 'react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import IcNext from '../../assets/imgs/ic_top.svg?react';
import useLocalizedTime from '../../hooks/useLocalizedTime';
import { SECTION_TYPE, TICKET_STATUS, TICKET_TYPE, USAGE_TICKET_STATUS } from '../../utils/Constants';
import { getValueByLanguage, shouldDisplayActivateDeadlineAt, shouldDisplayActivatedAt } from '../../utils';
import UseTicketButton from '../UseTicketButton/UseTicketButton';
import TextBreaks from '../TextBreaks/TextBreaks';

import styles from './style.module.css';
import { OrderTicket, OrderTicketList } from 'types';
import { OrderUsageTicket } from '@shared/orders/get/types';

type ComboTicketItemProps = {
    comboTicket: OrderTicketList;
    to: string;
};

/**
 * セット券チケット一覧に表示するもの
 */
const ComboTicketItem = memo(({ comboTicket, to }: ComboTicketItemProps) => {
    const { t } = useTranslation();
    const { formatTime } = useLocalizedTime();
    const navigate = useNavigate();

    const { reserveOn, originStopName, destStopName } = useMemo(() => {
        const reserveOn = comboTicket.orderUsageTickets?.[0]?.reserveOn;
        const originStopName = comboTicket.orderUsageTickets?.[0]?.originStopName;
        const destStopName = comboTicket.orderUsageTickets?.[0]?.destStopName;
        return {
            reserveOn,
            originStopName,
            destStopName,
        };
    }, [comboTicket]);
    // クリックして使用前詳細画面へ遷移
    const handleTicketClick = () => {
        navigate(to, { state: { orderId: comboTicket.orderId, orderTicketId: comboTicket.orderTicketId } });
    };

    const couponUseText = (couponTickets: OrderUsageTicket[]) => {
        if (couponTickets?.length > 0) {
            const beforeUseTotal = couponTickets.filter(
                (coupon) => coupon.status === USAGE_TICKET_STATUS.BOUGHT,
            ).length;
            const usingTotal = couponTickets.filter((coupon) => coupon.status === USAGE_TICKET_STATUS.ACTIVATED).length;
            const usedTotal = couponTickets.filter((coupon) => coupon.status === USAGE_TICKET_STATUS.USED).length;
            return (
                <>
                    <p>
                        {beforeUseTotal > 0
                            ? `${t('ticket-detail.before-use')}: ${t('ticket-detail.coupon-count', {
                                  count: beforeUseTotal,
                              })}(${t('ticket-detail.coupon-amount')}${t('ticket-detail.coupon-count', {
                                  count: couponTickets.length,
                              })})`
                            : `${t('ticket-detail.used')}: ${t('ticket-detail.coupon-count', { count: usedTotal })}`}
                    </p>
                    <p>
                        {usingTotal > 0
                            ? `${t('ticket-detail.in-use')}: ${t('ticket-detail.coupon-count', { count: usingTotal })}`
                            : ''}
                    </p>
                </>
            );
        } else {
            return '';
        }
    };

    return (
        <>
            <div className={styles.setTicketItemContent}>
                <div className={styles.info} onClick={handleTicketClick}>
                    <p className={styles.title}>
                        <TextBreaks text={getValueByLanguage(comboTicket.ticketName, i18next.language)} />
                    </p>
                    {comboTicket.isReserve ? (
                        <>
                            {comboTicket.sectionType === SECTION_TYPE.MULTI && originStopName && destStopName && (
                                <p className={styles.saleTicketName} style={{ marginBottom: '6px' }}>
                                    <TextBreaks text={getValueByLanguage(originStopName, i18next.language)} />
                                    <span style={{ margin: '0 5px' }}>→</span>
                                    <TextBreaks text={getValueByLanguage(destStopName, i18next.language)} />
                                </p>
                            )}
                            {reserveOn && (
                                <p className={styles.text}>
                                    {t('reservation.usage-time') +
                                        ': ' +
                                        t('daytime.tilde', { time: formatTime(reserveOn, 'lll') })}
                                </p>
                            )}
                        </>
                    ) : (
                        comboTicket.activateDeadlineAt &&
                        shouldDisplayActivateDeadlineAt(comboTicket as unknown as OrderTicket) && (
                            <p className={styles.text}>
                                {t('ticket-detail.activate-deadline') +
                                    ': ' +
                                    t('daytime.until', { time: formatTime(comboTicket.activateDeadlineAt, 'lll') })}
                            </p>
                        )
                    )}
                    {/*使用期限切れ*/}
                    {comboTicket.status === TICKET_STATUS.ACTIVATE_DEADLINE_PASSED && (
                        <p className={styles.text}>{t('ticket-detail.expired')}</p>
                    )}

                    {/*使用日時*/}
                    {shouldDisplayActivatedAt(comboTicket as unknown as OrderTicket) &&
                        comboTicket.orderUsageTickets?.[0]?.activatedAt &&
                        !comboTicket.isReserve && (
                            <p className={styles.text}>
                                {t('ticket-detail.activated-date') +
                                    ': ' +
                                    formatTime(comboTicket.orderUsageTickets[0]?.activatedAt, 'lll')}
                            </p>
                        )}

                    {/*有効期限*/}
                    {shouldDisplayActivatedAt(comboTicket as unknown as OrderTicket) &&
                        comboTicket.orderUsageTickets?.[0]?.expiredAt && (
                            <p className={styles.text}>
                                {t('ticket-detail.expired-at') +
                                    ': ' +
                                    t('daytime.until', {
                                        time: formatTime(comboTicket.orderUsageTickets[0]?.expiredAt, 'lll'),
                                    })}
                            </p>
                        )}
                    {comboTicket.ticketType === TICKET_TYPE.COUPON && comboTicket.orderUsageTickets && (
                        <div className={styles.text}>{couponUseText(comboTicket.orderUsageTickets)}</div>
                    )}
                    <div className={styles.toDetailBtn}>
                        <p className={styles.distributionsBtnText}>{t('ticket-detail.detail')}</p>
                        <IcNext className={styles.distributionsBtnIcon} />
                    </div>
                </div>
                <div className={styles.divider}></div>
                {<UseTicketButton ticket={comboTicket} />}
            </div>
        </>
    );
});

export default ComboTicketItem;
